import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Package, Plus, Trash } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BundlesPage() {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ bundle_name: '', sku: '', component_products: [{ product_id: '', quantity: 1 }], bundle_price: 0, is_virtual: false });

  useEffect(() => { fetchBundles(); }, []);

  const fetchBundles = async () => {
    try {
      const { data } = await axios.get(`${API}/api/seller/bundles`, { withCredentials: true });
      setBundles(data.bundles || []);
    } catch { toast.error('Failed to load bundles'); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.bundle_name || !form.sku) { toast.error('Name and SKU required'); return; }
    try {
      await axios.post(`${API}/api/seller/bundles`, form, { withCredentials: true });
      toast.success('Bundle created');
      setDialogOpen(false);
      setForm({ bundle_name: '', sku: '', component_products: [{ product_id: '', quantity: 1 }], bundle_price: 0, is_virtual: false });
      fetchBundles();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
  };

  const addComponent = () => setForm(f => ({ ...f, component_products: [...f.component_products, { product_id: '', quantity: 1 }] }));
  const removeComponent = (i) => setForm(f => ({ ...f, component_products: f.component_products.filter((_, idx) => idx !== i) }));
  const updateComponent = (i, field, val) => setForm(f => ({ ...f, component_products: f.component_products.map((c, idx) => idx === i ? { ...c, [field]: val } : c) }));

  return (
    <DashboardLayout>
      <div className="p-6 max-w-5xl" data-testid="bundles-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0A0A]">Product Bundles & Kits</h1>
            <p className="text-sm text-[#6b6b8a] mt-1">Create product bundles with special pricing</p>
          </div>
          <Button data-testid="add-bundle-btn" onClick={() => setDialogOpen(true)} className="bg-[#7d64ff] hover:bg-[#6b4fef]">
            <Plus size={16} className="mr-2" /> Create Bundle
          </Button>
        </div>

        {loading ? <div className="text-center py-12 text-[#9c9db7]">Loading...</div> : bundles.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-[#e5e5ea]">
            <Package size={48} className="mx-auto text-[#9c9db7] mb-3" />
            <p className="text-[#6b6b8a]">No bundles created yet.</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {bundles.map(b => (
              <div key={b.bundle_id} className="bg-white rounded-xl border border-[#e5e5ea] p-5" data-testid={`bundle-${b.bundle_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-[#0A0A0A]">{b.bundle_name}</h3>
                    <p className="text-xs font-mono text-[#9c9db7]">SKU: {b.sku}</p>
                  </div>
                  {b.is_virtual && <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700">Virtual</span>}
                </div>
                <p className="text-xl font-bold text-[#7d64ff] mb-2">&#8377;{b.bundle_price?.toLocaleString()}</p>
                <p className="text-xs text-[#6b6b8a]">{b.component_products?.length || 0} components</p>
                <div className="mt-2 space-y-1">
                  {(b.component_products || []).map((c, i) => (
                    <p key={i} className="text-xs text-[#3c3c64]">{c.product_id} &times; {c.quantity}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md" data-testid="bundle-dialog">
            <DialogHeader><DialogTitle>Create Bundle</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Bundle Name</Label><Input data-testid="bundle-name" value={form.bundle_name} onChange={e => setForm(f => ({ ...f, bundle_name: e.target.value }))} placeholder="Starter Kit" /></div>
              <div><Label>SKU</Label><Input value={form.sku} onChange={e => setForm(f => ({ ...f, sku: e.target.value }))} placeholder="BND-001" /></div>
              <div><Label>Bundle Price</Label><Input type="number" value={form.bundle_price} onChange={e => setForm(f => ({ ...f, bundle_price: parseFloat(e.target.value) || 0 }))} /></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_virtual} onChange={e => setForm(f => ({ ...f, is_virtual: e.target.checked }))} className="rounded" /> Virtual bundle (no physical assembly)</label>
              <div>
                <div className="flex items-center justify-between"><Label>Components</Label><Button variant="ghost" size="sm" onClick={addComponent} className="text-[#7d64ff] text-xs">+ Add</Button></div>
                <div className="space-y-2 mt-1">
                  {form.component_products.map((c, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <Input value={c.product_id} onChange={e => updateComponent(i, 'product_id', e.target.value)} placeholder="Product ID" className="flex-1" />
                      <Input type="number" value={c.quantity} onChange={e => updateComponent(i, 'quantity', parseInt(e.target.value) || 1)} className="w-16" />
                      {form.component_products.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeComponent(i)} className="text-red-500"><Trash size={14} /></Button>}
                    </div>
                  ))}
                </div>
              </div>
              <Button data-testid="save-bundle-btn" onClick={handleCreate} className="w-full bg-[#7d64ff] hover:bg-[#6b4fef]">Create Bundle</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
